import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Loader2,
  AlertTriangle,
  Settings,
  Check,
  Package,
  Shield,
} from "lucide-react";
import { SkeletonTable } from "./Skeleton";
import SeverityBadge, { Severity } from "./SeverityBadge";
import { FindingStatusBadge } from "./StatusBadge";
import { apiFetch } from "../../api/client";

interface ContainerFinding {
  id: string;
  fingerprint: string;
  status: string;
  severity: string;
  tool: string;
  vuln_id?: string;
  title?: string;
  package?: {
    name: string;
    ecosystem: string;
    installed_version: string;
    fixed_version?: string;
  };
  container?: {
    image_ref: string;
    target: string;
  };
  first_seen_at: string;
  last_seen_at: string;
}

interface ContainerFindingsResponse {
  repo_id: string;
  summary: {
    total: number;
    by_severity: Record<string, number>;
    by_status: Record<string, number>;
  };
  findings: ContainerFinding[];
}

interface RepoSettings {
  repo_id: string;
  url: string;
  container: {
    image: string | null;
    registry: string | null;
    enabled: boolean;
  };
  sbom_enabled: boolean;
}

interface ContainerTabProps {
  repoId: string;
}

async function fetchContainerFindings(repoId: string): Promise<ContainerFindingsResponse> {
  try {
    return await apiFetch<ContainerFindingsResponse>({
      path: `/v1/repos/${repoId}/findings/container`,
    });
  } catch (e: any) {
    if (e?.status === 404) {
      return { repo_id: repoId, summary: { total: 0, by_severity: {}, by_status: {} }, findings: [] };
    }
    throw e;
  }
}

async function fetchRepoSettings(repoId: string): Promise<RepoSettings> {
  return apiFetch<RepoSettings>({ path: `/v1/repos/${repoId}/settings` });
}

async function updateRepoSettings(repoId: string, settings: Partial<RepoSettings["container"]>): Promise<void> {
  await apiFetch({
    method: "PUT",
    path: `/v1/repos/${repoId}/settings`,
    body: {
      container_image: settings.image,
      container_enabled: settings.enabled,
    },
  });
}

export default function ContainerTab({ repoId }: ContainerTabProps) {
  const queryClient = useQueryClient();
  const [showSettings, setShowSettings] = useState(false);
  const [imageInput, setImageInput] = useState("");

  const findingsQuery = useQuery({
    queryKey: ["container-findings", repoId],
    queryFn: () => fetchContainerFindings(repoId),
    staleTime: 60000,
  });

  const settingsQuery = useQuery({
    queryKey: ["repo-settings", repoId],
    queryFn: () => fetchRepoSettings(repoId),
    staleTime: 60000,
  });

  const updateMutation = useMutation({
    mutationFn: (settings: Partial<RepoSettings["container"]>) => updateRepoSettings(repoId, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["repo-settings", repoId] });
      setShowSettings(false);
    },
  });

  const handleSaveSettings = () => {
    updateMutation.mutate({
      image: imageInput,
      enabled: true,
    });
  };

  React.useEffect(() => {
    if (settingsQuery.data?.container?.image) {
      setImageInput(settingsQuery.data.container.image);
    }
  }, [settingsQuery.data]);

  const isLoading = findingsQuery.isLoading || settingsQuery.isLoading;
  const containerEnabled = settingsQuery.data?.container?.enabled;
  const containerImage = settingsQuery.data?.container?.image;
  const findings = findingsQuery.data?.findings || [];
  const summary = findingsQuery.data?.summary || { total: 0, by_severity: {}, by_status: {} };

  if (isLoading) {
    return <SkeletonTable rows={5} columns={6} />;
  }

  // Show settings prompt if container scanning not configured
  if (!containerEnabled || !containerImage) {
    return (
      <div className="space-y-6">
        <div className="card p-8 text-center">
          <Box className="h-12 w-12 text-muted mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text">Container Scanning Not Configured</h3>
          <p className="text-sm text-muted mt-2 max-w-md mx-auto">
            Configure a Docker image to scan for OS-level vulnerabilities in your container.
          </p>
          <button
            onClick={() => setShowSettings(true)}
            className="btn-primary mt-6"
          >
            <Settings className="h-4 w-4" />
            Configure Container Scanning
          </button>
        </div>

        {/* Settings Modal */}
        {showSettings && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="card w-full max-w-md p-6 mx-4">
              <h3 className="text-lg font-semibold text-text mb-4">Container Scanning Settings</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-muted mb-2">
                    Docker Image Reference
                  </label>
                  <input
                    type="text"
                    value={imageInput}
                    onChange={(e) => setImageInput(e.target.value)}
                    placeholder="e.g., nginx:1.21, myorg/myapp:latest, 123456.dkr.ecr.us-east-1.amazonaws.com/myapp:v1"
                    className="input w-full"
                  />
                  <p className="text-xs text-muted mt-2">
                    Supports Docker Hub, ECR, GCR, and other registries.
                  </p>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={() => setShowSettings(false)}
                    className="btn-ghost"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveSettings}
                    disabled={!imageInput || updateMutation.isPending}
                    className="btn-primary"
                  >
                    {updateMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    Save & Enable
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Show findings
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-text flex items-center gap-2">
            <Box className="h-5 w-5 text-brand" />
            Container Vulnerabilities
          </h2>
          <p className="text-sm text-muted mt-1">
            Image: <code className="bg-surface2 px-2 py-0.5 rounded text-xs">{containerImage}</code>
          </p>
        </div>

        <button
          onClick={() => setShowSettings(true)}
          className="btn-ghost"
        >
          <Settings className="h-4 w-4" />
          Settings
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <div className="card p-5">
          <div className="text-sm font-medium text-muted mb-1">Total</div>
          <div className="text-2xl font-bold text-text">{summary.total}</div>
        </div>
        <div className="card p-5 border-l-4 border-critical">
          <div className="text-sm font-medium text-muted mb-1">Critical</div>
          <div className="text-2xl font-bold text-critical">{summary.by_severity.critical || 0}</div>
        </div>
        <div className="card p-5 border-l-4 border-high">
          <div className="text-sm font-medium text-muted mb-1">High</div>
          <div className="text-2xl font-bold text-high">{summary.by_severity.high || 0}</div>
        </div>
        <div className="card p-5 border-l-4 border-medium">
          <div className="text-sm font-medium text-muted mb-1">Medium</div>
          <div className="text-2xl font-bold text-medium">{summary.by_severity.medium || 0}</div>
        </div>
        <div className="card p-5 border-l-4 border-low">
          <div className="text-sm font-medium text-muted mb-1">Low</div>
          <div className="text-2xl font-bold text-low">{summary.by_severity.low || 0}</div>
        </div>
      </div>

      {/* Findings List */}
      {findings.length === 0 ? (
        <div className="card p-12 text-center">
          <Shield className="h-12 w-12 text-success mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text">No Container Vulnerabilities</h3>
          <p className="text-sm text-muted mt-2">
            Great! The container image has no known OS-level vulnerabilities.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-divider bg-surface2/50">
                <th className="text-left px-4 py-3 text-xs font-medium text-muted uppercase tracking-wide">
                  Vulnerability
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted uppercase tracking-wide">
                  Package
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted uppercase tracking-wide hidden lg:table-cell">
                  Installed
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted uppercase tracking-wide hidden lg:table-cell">
                  Fixed In
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted uppercase tracking-wide">
                  Severity
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted uppercase tracking-wide">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {findings.map((f) => (
                <tr key={f.id} className="hover:bg-surface2/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-text">{f.vuln_id || "Unknown"}</div>
                    <div className="text-xs text-muted mt-1 truncate max-w-xs">{f.title}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted" />
                      <span className="font-medium text-text">{f.package?.name || "Unknown"}</span>
                    </div>
                    <div className="text-xs text-muted mt-1">{f.package?.ecosystem}</div>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <code className="text-xs bg-surface2 px-2 py-1 rounded">
                      {f.package?.installed_version || "N/A"}
                    </code>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {f.package?.fixed_version ? (
                      <code className="text-xs bg-success/10 text-success px-2 py-1 rounded">
                        {f.package.fixed_version}
                      </code>
                    ) : (
                      <span className="text-xs text-muted">No fix</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <SeverityBadge severity={f.severity as Severity} />
                  </td>
                  <td className="px-4 py-3">
                    <FindingStatusBadge status={f.status as "open" | "fixed" | "suppressed"} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="card w-full max-w-md p-6 mx-4">
            <h3 className="text-lg font-semibold text-text mb-4">Container Scanning Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted mb-2">
                  Docker Image Reference
                </label>
                <input
                  type="text"
                  value={imageInput}
                  onChange={(e) => setImageInput(e.target.value)}
                  placeholder="e.g., nginx:1.21, myorg/myapp:latest"
                  className="input w-full"
                />
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowSettings(false)}
                  className="btn-ghost"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSettings}
                  disabled={!imageInput || updateMutation.isPending}
                  className="btn-primary"
                >
                  {updateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
